import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import CategoryTabs from '@/components/feature/CategoryTabs';
import { getMangaByCategory, categories as allCategories } from '@/mocks/manga';
import { categoryInfo } from '@/mocks/categories';

const categorySlugMap: Record<string, string> = {
  'all': '全部',
  'romance': '恋爱',
  'action': '热血',
  'fantasy': '奇幻',
  'mystery': '悬疑',
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const categoryChinese = categorySlugMap[slug] || '全部';
  const [activeCategory, setActiveCategory] = useState(categoryChinese);

  useEffect(() => {
    setActiveCategory(categoryChinese);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const mangaList = getMangaByCategory(activeCategory);
  const info = categoryInfo[activeCategory];

  const handleCategoryChange = (cat: string) => {
    const reverseMap: Record<string, string> = {};
    Object.entries(categorySlugMap).forEach(([key, value]) => {
      reverseMap[value] = key;
    });
    const newSlug = reverseMap[cat] || 'all';
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveCategory(cat);
    window.REACT_APP_NAVIGATE(`/category/${newSlug}`);
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${info?.name || '全部漫画'} - 妖精漫画`,
    description: info?.description || '',
    url: `https://www.yaojingmanhua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '妖精漫画',
      url: 'https://www.yaojingmanhua.com/',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: info?.name || '全部漫画' },
  ];

  return (
    <>
      <SEOHead
        title={`${info?.name || '全部漫画'} - 妖精漫画`}
        description={info?.description || ''}
        keywords={`妖精漫画,韩国漫画,${activeCategory}漫画,免费漫画`}
        canonicalUrl={`/category/${slug}`}
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {info?.name || '全部漫画'}
            </h1>
            <p className="text-sm text-foreground-500 mb-6 max-w-2xl">{info?.description}</p>

            <CategoryTabs
              categories={allCategories}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
            />

            <div className="mt-8">
              {mangaList.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                  {mangaList.map((manga) => (
                    <MangaCard key={manga.slug} manga={manga} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                  <Link to="/category/all" className="mt-3 inline-block text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">
                    查看全部漫画
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}